<template>
    <v-dialog :value="value" max-width="560" persistent @input="$emit('input', $event)">
        <v-card>
            <v-card-title class="text-h6">{{ finalizeDialogTitle }}</v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-4">
                <p class="body-2 mb-4">{{ finalizeDialogIntro }}</p>
                <v-textarea
                    v-model="observacaoExterna"
                    :label="$t('ticket.tasks.externalObservation')"
                    rows="3"
                    outlined
                    dense
                    hide-details="auto"
                />
                <v-textarea
                    v-if="showInternalObservation"
                    v-model="observacaoInterna"
                    :label="$t('ticket.tasks.internalObservation')"
                    rows="2"
                    outlined
                    dense
                    hide-details="auto"
                    class="mt-2"
                />
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text :disabled="loadingClose" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn
                    color="primary"
                    :disabled="idStatus == null || loadingClose"
                    :loading="loadingClose"
                    @click="confirm"
                >
                    {{ finalizeDialogConfirmLabel }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "../../utils/eventBus";

/**
 * Diálogo de conclusão/cancelamento de ticket (evento request-close-ticket do TicketsJiraView).
 * Uso: chamar open(payload) via ref; payload é o ticket com finalizeMode 'conclude' | 'cancel'.
 */
export default {
    name: 'TicketCloseDialog',
    props: {
        value: { type: Boolean, default: false },
        user: { type: Object, default: null },
        showInternalObservation: { type: Boolean, default: false },
    },
    data: () => ({
        statusCatalog: [],
        ticketToClose: null,
        finalizeMode: null,
        idStatus: null,
        observacaoExterna: '',
        observacaoInterna: '',
        loadingClose: false,
    }),
    computed: {
        finalizeDialogTitle() {
            if (this.finalizeMode === 'cancel') return this.$t('ticket.finalizeCancelTitle');
            return this.$t('ticket.finalizeConcludeTitle');
        },
        finalizeDialogIntro() {
            const statusName =
                this.finalizeMode === 'cancel'
                    ? this.$t('ticket.finalizeStatusNameCancel')
                    : this.$t('ticket.finalizeStatusNameConclude');
            return this.$t('ticket.finalizeIntro', { status: statusName });
        },
        finalizeDialogConfirmLabel() {
            if (this.finalizeMode === 'cancel') return this.$t('ticket.finalizeConfirmCancel');
            return this.$t('ticket.finalizeConfirmConclude');
        },
    },
    methods: {
        async loadStatusCatalog() {
            if (this.statusCatalog.length > 0) return;
            try {
                const response = await apiController.request(apiController.endpoints.getallStatus, { silent: true });
                this.statusCatalog = Array.isArray(response.data) ? response.data : [];
            } catch (e) {
                this.statusCatalog = [];
            }
        },
        statusIdByCatalogName(statusTicketName) {
            const want = String(statusTicketName || '').trim().toLowerCase();
            const row = this.statusCatalog.find((s) => {
                if (!s || s.status_ticket == null) return false;
                return String(s.status_ticket).trim().toLowerCase() === want;
            });
            return row && row.id_status_ticket != null ? Number(row.id_status_ticket) : null;
        },
        async open(payload) {
            const mode = payload && payload.finalizeMode;
            if (mode !== 'conclude' && mode !== 'cancel') {
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: this.$t('ticket.alerts.error.finalizeInvalidMode')
                });
                return;
            }
            await this.loadStatusCatalog();
            const statusName = mode === 'cancel' ? 'Cancelado' : 'Concluído';
            const idStatus = this.statusIdByCatalogName(statusName);
            if (idStatus == null) {
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: this.$t('ticket.alerts.error.finalizeStatusMissing', { status: statusName })
                });
                return;
            }
            const ticket = { ...payload };
            delete ticket.finalizeMode;
            this.ticketToClose = ticket;
            this.finalizeMode = mode;
            this.idStatus = idStatus;
            this.observacaoExterna = '';
            this.observacaoInterna = '';
            this.$emit('input', true);
        },
        close() {
            this.$emit('input', false);
            this.ticketToClose = null;
            this.finalizeMode = null;
            this.idStatus = null;
            this.observacaoExterna = '';
            this.observacaoInterna = '';
        },
        confirm() {
            if (!this.ticketToClose || this.idStatus == null) return;
            this.loadingClose = true;

            const itemToUpdate = Object.assign({}, this.ticketToClose);
            itemToUpdate.id_usuario_ultima_alteracao = this.user && this.user.id_usuario;
            itemToUpdate.finalizado = 1;
            // Board do cliente/agência usa id_status_atribuido primeiro; o do despachante, id_status_dispatcher.
            // Finalizar atualiza os dois para o card sair de "Aberto" em todas as visões.
            itemToUpdate.id_status_dispatcher = this.idStatus;
            itemToUpdate.id_status_atribuido = this.idStatus;
            const ext = this.observacaoExterna && String(this.observacaoExterna).trim();
            const int = this.showInternalObservation && this.observacaoInterna && String(this.observacaoInterna).trim();
            itemToUpdate.observacao_fechamento_externa = ext || null;
            itemToUpdate.observacao_fechamento_interna = int || null;
            const cancelado = this.finalizeMode === 'cancel';

            apiController.request(apiController.endpoints.updateTicket, {
                data: itemToUpdate,
                onSuccess: (response) => {
                    this.loadingClose = false;
                    if (response.data.erro) {
                        EventBus.$emit('show-alert', { tipo: 'error', mensagem: this.$t('ticket.alerts.error.status') + response.data.mensagem });
                        return;
                    }
                    EventBus.$emit('show-alert', {
                        tipo: 'success',
                        mensagem: cancelado
                            ? this.$t('ticket.alerts.success.finalizedCancelled')
                            : this.$t('ticket.alerts.success.finalizedConcluded'),
                    });
                    this.close();
                    this.$emit('closed');
                    this.$root.$emit("atualizarTicketsAbertos");
                },
                onError: () => {
                    this.loadingClose = false;
                },
            }).catch(() => {});
        },
    },
};
</script>
